'use client'

import * as React from 'react'

import { useInfiniteSocialListPostByProductQuery } from './use-infinite-post-a-product'
import { Container } from './container'
import { tw } from 'utils/classnames'

import { Slide } from './slide'
import { Controls } from './controls'
import { CarouselContextProvider, useCarouselContext } from './context'
import { useTranslations } from 'next-intl'
import type { TranslationValues } from 'next-intl'
import { Typography } from '@repo/design-system-cf/components/typography'
import type { SocialListPostByProductQuery } from '@web/gen/feed-post'

type PostType = NonNullable<
  NonNullable<SocialListPostByProductQuery['socialListPostByProduct']>['Posts'][number]
>
type TranslateFn = (key: string, values?: TranslationValues) => string

function LoadMoreTrigger({
  canLoadMore,
  showMore,
}: {
  canLoadMore: boolean
  showMore: () => void
}) {
  const sentinelRef = React.useRef<HTMLDivElement>(null)
  const [hasTriggered, setHasTriggered] = React.useState(false)
  const { containerRef } = useCarouselContext()
  const autoLoadAttemptsRef = React.useRef(0)

  React.useEffect(() => {
    if (!sentinelRef.current || hasTriggered) {
      return
    }

    const container = containerRef?.current ?? sentinelRef.current.parentElement
    if (!container) {
      return
    }

    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth + 1

    // Auto-load pages until overflow exists (or no more pages), limited attempts
    if (!hasHorizontalOverflow && canLoadMore && autoLoadAttemptsRef.current < 5) {
      autoLoadAttemptsRef.current += 1
      setHasTriggered(true)
      showMore()
      const timer = setTimeout(() => {
        setHasTriggered(false)
      }, 400)
      return () => clearTimeout(timer)
    }

    if (!hasHorizontalOverflow) {
      // No overflow and can't load more → nothing to observe
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && canLoadMore && !hasTriggered) {
            setHasTriggered(true)
            showMore()
            // Reset after a delay to allow for next trigger
            setTimeout(() => setHasTriggered(false), 1000)
          }
        })
      },
      {
        root: container,
        rootMargin: '0px 100px 0px 0px',
        threshold: 0,
      },
    )

    observer.observe(sentinelRef.current)

    return () => {
      observer.disconnect()
    }
  }, [canLoadMore, showMore, hasTriggered, containerRef])

  return (
    <div
      ref={sentinelRef}
      className="load-more-trigger h-full w-4 flex-shrink-0"
      aria-hidden="true"
    />
  )
}

const PAGE_SIZE = 8

export const ShareGallery = ({ productId }: { productId: string }) => {
  const t = useTranslations('prod.product_description_page.share_creation.share_gallery')

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteSocialListPostByProductQuery({
      input: {
        productId,
        pageSize: PAGE_SIZE,
        cursor: 0,
      },
    })

  const posts: PostType[] = React.useMemo(() => {
    const items = data?.pages?.flatMap(page => page.socialListPostByProduct?.Posts ?? []) ?? []
    // oxlint-disable-next-line eqeqeq
    return items.filter((p): p is PostType => p != null)
  }, [data])

  if (isPending || !data?.pages?.[0]?.socialListPostByProduct?.Posts?.length) {
    return null
  }

  return (
    <CarouselContextProvider>
      <ShareGalleryContent
        posts={posts}
        hasNextPage={hasNextPage ?? false}
        isFetchingNextPage={isFetchingNextPage ?? false}
        fetchNextPage={fetchNextPage}
        t={t}
      />
    </CarouselContextProvider>
  )
}

function ShareGalleryContent({
  posts,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  t,
}: {
  posts: PostType[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  t: TranslateFn
}) {
  return (
    <section
      className={tw('rounded-2 bg-cf-white w-full mb-4  relative min-w-full  mt-6')}
      id="post-a-project-carousel"
    >
      <div className="flex items-center justify-between">
        <Typography variant={'cf-title-03'} asChild>
          <h3>{t('title')}</h3>
        </Typography>
        <Controls key="controls" />
      </div>
      <Container>
        {posts.map(post => {
          return <Slide key={post.id} post={post} />
        })}
        <LoadMoreTrigger
          canLoadMore={Boolean(hasNextPage && !isFetchingNextPage)}
          showMore={fetchNextPage}
        />
      </Container>
    </section>
  )
}
